import axios from "axios";
import {
  refreshAccessToken,
  clearAuthState,
  REFRESH_ENDPOINT,
  LOGOUT_ENDPOINT,
} from "./authService";

/**
 * Installs a global response interceptor implementing the cookie-based token
 * refresh + retry flow. When an authenticated request fails with 401, we
 * silently refresh the access token via the HttpOnly refresh cookie, update the
 * in-memory token, and retry the original request once. If the refresh fails,
 * the user is treated as unauthenticated and redirected to the login flow.
 */
export const setupAxiosInterceptors = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;
      const url = originalRequest?.url || "";

      const isAuthEndpoint =
        url.includes(REFRESH_ENDPOINT) ||
        url.includes(LOGOUT_ENDPOINT) ||
        url.includes("/login");

      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint
      ) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken();

          // Re-apply the refreshed access token, preserving the existing header
          // scheme used by the original request (raw token vs. "Bearer ...").
          originalRequest.headers = originalRequest.headers || {};
          const prev = originalRequest.headers.Authorization;
          originalRequest.headers.Authorization =
            typeof prev === "string" && prev.startsWith("Bearer ")
              ? `Bearer ${newToken}`
              : newToken;

          return axios(originalRequest);
        } catch (refreshError) {
          clearAuthState();
          if (window.location.pathname !== "/login") {
            window.location.assign("/login");
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
